import React, { useEffect, useState } from "react";
import { subscribeBusy } from "../../utils/mutationGuard";

// Shown while a save / create / delete / approve is running, so nobody keeps
// clicking or presses Ctrl+S again and it is obvious the app is working. Quick
// saves (under SHOW_AFTER_MS) never flash it. Pairs with utils/mutationGuard.js,
// which is what actually prevents a duplicate request.
const SHOW_AFTER_MS = 300;

// Messages step forward the longer the request runs, so a slow save feels alive
// instead of stuck.
const STAGES = [
  { after: 0, title: "Processing your request", hint: "Please wait a moment. Avoid clicking again." },
  { after: 3000, title: "Please wait", hint: "This is taking a little longer than usual." },
  { after: 8000, title: "Almost done", hint: "Thanks for your patience." },
  { after: 20000, title: "Still processing", hint: "The server is busy. Please don't close or refresh this page." },
];

const CSS = `
@keyframes alo-fade { from { opacity: 0 } to { opacity: 1 } }
@keyframes alo-pop { from { opacity: 0; transform: translateY(10px) scale(.96) } to { opacity: 1; transform: none } }
@keyframes alo-spin { to { transform: rotate(360deg) } }
@keyframes alo-pulse { 0% { transform: scale(.85); opacity: .55 } 100% { transform: scale(1.6); opacity: 0 } }
@keyframes alo-slide { 0% { transform: translateX(-100%) } 100% { transform: translateX(350%) } }
@keyframes alo-dot { 0%, 80%, 100% { opacity: .2 } 40% { opacity: 1 } }
@keyframes alo-text { from { opacity: 0; transform: translateY(4px) } to { opacity: 1; transform: none } }
.alo-dot { animation: alo-dot 1.2s infinite both; }
@media (prefers-reduced-motion: reduce) {
  .alo-card *, .alo-card { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; }
}
`;

const ActionLockOverlay = () => {
  const [busy, setBusy] = useState(0);
  const [visible, setVisible] = useState(false);
  const [stage, setStage] = useState(0);

  useEffect(() => subscribeBusy(setBusy), []);

  useEffect(() => {
    if (!busy) {
      setVisible(false);
      setStage(0);
      return undefined;
    }
    const timer = setTimeout(() => setVisible(true), SHOW_AFTER_MS);
    return () => clearTimeout(timer);
  }, [busy]);

  useEffect(() => {
    if (!visible) return undefined;
    const timers = STAGES.slice(1).map((s, i) =>
      setTimeout(() => setStage(i + 1), s.after - SHOW_AFTER_MS)
    );
    return () => timers.forEach(clearTimeout);
  }, [visible]);

  if (!visible) return null;

  const { title, hint } = STAGES[stage];

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 20000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(15, 23, 42, 0.28)",
        backdropFilter: "blur(3px)",
        cursor: "progress",
        animation: "alo-fade .2s ease-out",
      }}
    >
      <style>{CSS}</style>
      <div
        className="alo-card"
        style={{
          width: 300,
          maxWidth: "88vw",
          padding: "26px 28px 22px",
          borderRadius: 18,
          background: "#ffffff",
          boxShadow: "0 24px 60px -16px rgba(15, 23, 42, 0.45)",
          border: "1px solid #fed7aa",
          textAlign: "center",
          overflow: "hidden",
          animation: "alo-pop .25s cubic-bezier(.2,.8,.2,1)",
        }}
      >
        <div style={{ position: "relative", width: 56, height: 56, margin: "0 auto 16px" }}>
          <span
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: "#fb923c",
              animation: "alo-pulse 1.6s ease-out infinite",
            }}
          />
          <span
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              border: "4px solid #ffedd5",
              borderTopColor: "#ea580c",
              borderRightColor: "#fb923c",
              animation: "alo-spin .9s linear infinite",
              background: "#fff",
            }}
          />
          <span
            style={{
              position: "absolute",
              inset: 18,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #fb923c, #ea580c)",
            }}
          />
        </div>

        <div key={stage} style={{ animation: "alo-text .3s ease-out" }}>
          <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "1.02rem" }}>
            {title}
            {[0, 1, 2].map((i) => (
              <span key={i} className="alo-dot" style={{ animationDelay: `${i * 0.2}s` }}>
                .
              </span>
            ))}
          </div>
          <div style={{ color: "#64748b", fontSize: "0.82rem", marginTop: 4, lineHeight: 1.4 }}>{hint}</div>
        </div>

        <div
          style={{
            position: "relative",
            height: 4,
            borderRadius: 4,
            background: "#ffedd5",
            marginTop: 18,
            overflow: "hidden",
          }}
        >
          <span
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              height: "100%",
              width: "30%",
              borderRadius: 4,
              background: "linear-gradient(90deg, #fb923c, #ea580c)",
              animation: "alo-slide 1.3s ease-in-out infinite",
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ActionLockOverlay;
